// Colour-by wiring for the Area detail page. The modal is shared; this supplies the
// attributes, the surfaces a rule can paint, and the value lists.
import type {
  ColorByApplyTarget,
  ColorByCategory,
  ColorByOption,
} from 'components/color-by-attribute-modal';
import type { WellsFacetGroup } from 'types/pre-drill-workspace';
import { WELL_STATUS_COLORS } from './well-statuses';

export interface AreaWellColorMapInput {
  /** Every well the boundary caught, each carrying its status. */
  wells: Array<{ uwi: string; status?: string | null }>;
  /** The legend's colours, keyed by the lower-case status. */
  statusColors: Record<string, string>;
  /** UWI → colour from the enabled colour rules; these win. */
  ruleColors: Record<string, string>;
}

// UWI → colour: the legend's status colours as a base coat, a colour rule over the
// top. Statuses are downcased — the query returns `PRODUCING`, the legend keys `producing`.
export const areaWellColorMap = ({
  wells,
  statusColors,
  ruleColors,
}: AreaWellColorMapInput): Record<string, string> => {
  const byStatus: Record<string, string> = {};
  wells.forEach((well) => {
    if (!well.uwi || !well.status) {
      return;
    }
    const status = well.status.toLowerCase();
    const color = statusColors[status] ?? WELL_STATUS_COLORS[status];
    if (color) {
      byStatus[well.uwi] = color;
    }
  });
  return { ...byStatus, ...ruleColors };
};

// What an area's wells can be coloured by. Kept to the attributes the wells facet call
// serves and the table already shows, so every option resolves to real values.
export const AREA_COLOR_CATEGORIES: ColorByCategory[] = [
  { id: 'status', label: 'Well Status' },
  { id: 'currentOperator', label: 'Current Operator' },
  { id: 'holeDirection', label: 'Hole Direction' },
];

/** The one surface an area rule paints — the detail page's map. */
export const AREA_COLOR_TARGETS: ColorByApplyTarget[] = [
  { id: 'map', label: 'Map' },
];

export const AREA_COLOR_NEW_RULE_APPLY_TO = { map: true };
export const AREA_COLOR_RESET_APPLY_TO = { map: true };

// Attribute id (as the facets response keys it) → the wells field to request. Mirrors
// pre-drill's map, trimmed to the attributes offered above.
export const AREA_COLOR_FACET_PARAM: Record<string, string> = {
  status: 'status',
  currentOperator: 'current_operator',
  holeDirection: 'hole_direction',
};

export const AREA_COLOR_FACET_FIELDS = Object.values(AREA_COLOR_FACET_PARAM);

// The modal's picklist for one attribute. Buckets already carry `{id, name}`, so this
// is a pass-through with an empty fallback where the server had no values.
export const facetOptions = (
  facets: Record<string, WellsFacetGroup | null> | null,
  categoryId: string
): ColorByOption[] =>
  (facets?.[categoryId]?.buckets ?? []).map((bucket) => ({
    id: bucket.id,
    name: bucket.name,
  }));

// The label for an attribute id, for the applied-rule list.
export const colorCategoryLabel = (attribute: string): string =>
  AREA_COLOR_CATEGORIES.find((category) => category.id === attribute)?.label ??
  attribute;

// The label for an apply-target id, for the "Active on" chips.
export const colorTargetLabel = (target: string): string =>
  AREA_COLOR_TARGETS.find((candidate) => candidate.id === target)?.label ??
  target;
